import { useCallback } from "react";
import { DataProvider, Identifier, Record, useUnselectAll } from "react-admin";
import { filter } from "../../common/components/utils/fn";
import { count, create, findAndAction, findOne, formAction, getOne, sendMail, update } from "../common/common";
import { Lot } from "../lot/actions";
import { ProductionUnit, productionUnitIsMaybeDone } from "../productionUnit/actions";
import { Project, StageInfos } from "../project/actions";
import { Stage } from "../workflow/reader";
import { translateTime } from "./list";

export interface Task extends Record {
    parentTaskId?: Identifier[] | string;
    taskStatus: string;
    stageId: Identifier;
    productionUnitId: Identifier;
    estimateTime: number;
    availableDate: Date;
    assignedDate?: Date;
    endDate?: Date;
    commentTask?: string;
    comment?: string;
    assignTo?: Identifier;
    projectId: Identifier;
    quantity: number;
    quantityTask?: number;
    productionFile?: Identifier;
    productionFileExternal?: string;
    shuttleFile?: Identifier;
    previousProductionFile?: Identifier[] | string;
    previousProductionFileExternal?: string[] | string;
    previousShuttleFile?: Identifier[] | string;
    subprojectLotId?: Identifier;
    stageInfosId: Identifier; // this not required but will make easier next queries
}

export const coefficientInMinutes = {
    minute: 1,
    hour: 60
};

const checkNeedStayWaiting = async (dataProvider: DataProvider, stage: Stage, productionUnitId: Identifier, previousTaskId?: Identifier) => {
    let needBeWaiting = false;
    if (stage.incoming.length > 0) {
        for (const stageId of stage.incoming) {
            if (stageId !== previousTaskId) {
                const totalNotFinish: number = await count(dataProvider, "task", { stageId, productionUnitId, taskStatus: ["available", "assigned", "waiting"] });
                if (totalNotFinish > 0) {
                    needBeWaiting = true;
                    break;
                }
                const totalFinish: number = await count(dataProvider, "task", { stageId, productionUnitId, taskStatus: ["completed"] });
                if (totalFinish === 0) {
                    needBeWaiting = true;
                    break;
                }
            }
        }
    }
    return needBeWaiting;
}

const sendMailTaskAvailable = (task) => {
    if (task?.stage?.isStarting === false) {
        const team = task.team ? task.team : task.stage_info.team;
        sendMail("task/available", {
            to: team.manager.email,
            fullname: team.manager.fullname,
            taskId: task.id,
            stageName: task.stage.name,
            productionName: task.production_unit.name,
            lotName: task.production_unit.lot.name,
            projectName: task.production_unit.lot.project.name,
            unitProd: task.production_unit.lot.project.unitProd,
            unitMeasure: task.production_unit.lot.project.unitMeasure,
            quantity: task.quantity,
            estimateTime: `${task.estimateTime} minutes`
        });
    }
};

const createTask = async (dataProvider: DataProvider, stage: Stage, project: Project, lot: Lot, productionUnit: ProductionUnit, ratioInMinutes: number, stageInfos: StageInfos, previousTask?: Task, where?: string) => {
    let availableDate = new Date();
    let taskStatus = "available";
    const previousTaskId = previousTask ? previousTask.id : undefined;
    if (await checkNeedStayWaiting(dataProvider, stage, productionUnit.id, previousTaskId)) {
        taskStatus = "waiting";
        availableDate = undefined;
    }
    const quantity = (previousTask && previousTask.quantityTask) || (previousTask && previousTask.quantity) || productionUnit.quantity;
    const estimateTime = Math.round(ratioInMinutes * quantity);
    const data: Task = {
        id: undefined,
        taskStatus,
        stageId: stage.id,
        productionUnitId: productionUnit.id,
        estimateTime,
        availableDate,
        projectId: project.id,
        stageInfosId: stageInfos.id,
        quantity
    };
    let comment;
    if (previousTask) {
        data.parentTaskId = `{${[previousTaskId]}}`;
        data.previousShuttleFile = `{${previousTask.shuttleFile ? [previousTask.shuttleFile] : []}}`;
        data.previousProductionFileExternal = `{${previousTask.productionFileExternal ? [previousTask.productionFileExternal] : (previousTask.previousProductionFileExternal ? previousTask.previousProductionFileExternal : [])}}`;
        data.previousProductionFile = `{${previousTask.productionFile ? [previousTask.productionFile] : (previousTask.previousProductionFile ? previousTask.previousProductionFile : [])}}`;
        comment = previousTask.comment || "";
        if (previousTask.commentTask) {
            if (comment !== "") {
                comment += "\n\n";
            }
            comment += await getCommentTask(dataProvider, previousTask);
        }
        if (where === "outgoing") {
            if (previousTask.nextTeamAssignTo) {
                data.teamAssignTo = previousTask.nextTeamAssignTo;
            }
        } else if (where === "outgoingError") {
            if (previousTask.rejectTeamAssignTo) {
                data.teamAssignTo = previousTask.rejectTeamAssignTo;
            }
        }
    } else {
        comment = productionUnit.comment;
    }
    if (comment !== "") data.comment = comment;

    if (stageInfos.isSubproject) {
        // We create the lot automaticaly
        const subprojectLot = await create(dataProvider, "lot", {
            name: `[${project.name}][${lot.name}] ${productionUnit.name}`,
            startDate: lot.startDate,
            endDate: lot.endDate,
            projectId: stageInfos.subprojectId
        });
        data.subprojectLotId = subprojectLot.id;
    }


    const task = await create(dataProvider, "task", data);
    if (task.taskStatus === "available") {
        sendMailTaskAvailable(task);
    }
    return task;
};

const getCommentTask = async (dataProvider: DataProvider, task: Task) => {
    const stage = await findOne(dataProvider, "stage", { id: task.stageId });
    return `- ${stage.name} (${new Date().toLocaleDateString()}) :\n${task.commentTask}`;
}

const updateTask = async (dataProvider: DataProvider, task: Task, stage: Stage, productionUnitId: Identifier, previousTask?: Task) => {
    const previousTaskId = previousTask ? previousTask.id : undefined;
    let dataToUpdate: any = {};
    if (previousTask) {
        let parentTaskId: Identifier[] | string = task.parentTaskId || [];
        let previousProductionFileExternal = task.previousProductionFileExternal || [];
        let previousProductionFile = task.previousProductionFile || [];
        let previousShuttleFile = task.previousShuttleFile || [];
        if (typeof parentTaskId !== "string") {
            parentTaskId = [...parentTaskId];
            parentTaskId.push(previousTaskId);
            dataToUpdate.parentTaskId = `{${parentTaskId}}`;
        } else {
            dataToUpdate.parentTaskId = `{${previousTaskId},${parentTaskId.substring(1)}}`;
        }
        if (previousTask.shuttleFile) {
            if (typeof previousShuttleFile !== "string") {
                previousShuttleFile = [...previousShuttleFile];
                previousShuttleFile.push(previousTask.shuttleFile);
                dataToUpdate.previousShuttleFile = `{${previousShuttleFile}}`;
            } else {
                dataToUpdate.previousShuttleFile = `{${previousTask.shuttleFile},${previousShuttleFile.substring(1)}`;
            }
        }
        if (previousTask.productionFile) {
            if (typeof previousProductionFile !== "string") {
                previousProductionFile = [...previousProductionFile];
                previousProductionFile.push(previousTask.productionFile);
                dataToUpdate.previousProductionFile = `{${previousProductionFile}}`;
            } else {
                dataToUpdate.previousProductionFile = `{${previousTask.productionFile},${previousProductionFile.substring(1)}`;
            }
        } else if (previousTask.previousProductionFile) {
            if (typeof previousTask.previousProductionFile !== "string") {
                if (typeof previousProductionFile !== "string") {
                    for (const file of previousTask.previousProductionFile) {
                        previousProductionFile = [...previousProductionFile];
                        previousProductionFile.push(file);
                    }
                    dataToUpdate.previousProductionFile = `{${previousProductionFile}}`;
                } else {
                    dataToUpdate.previousProductionFile = `{${previousTask.previousProductionFile.join(",")},${previousProductionFile.substring(1)}`;
                }
            }
        }
        if (previousTask.productionFileExternal) {
            if (typeof previousProductionFileExternal !== "string") {
                previousProductionFileExternal = [...previousProductionFileExternal];
                if (previousProductionFileExternal.indexOf(previousTask.productionFileExternal) === -1) {
                    previousProductionFileExternal.push(previousTask.productionFileExternal);
                }
                dataToUpdate.previousProductionFileExternal = `{${previousProductionFileExternal}}`;
            } else {
                if (previousProductionFileExternal.indexOf(previousTask.productionFileExternal) === -1) {
                    dataToUpdate.previousProductionFileExternal = `{${previousTask.productionFileExternal},${previousProductionFileExternal.substring(1)}`;
                } else {
                    dataToUpdate.previousProductionFileExternal = previousProductionFileExternal;
                }
            }
        } else if (previousTask.previousProductionFileExternal) {
            if (typeof previousTask.previousProductionFileExternal !== "string") {
                if (typeof previousProductionFileExternal !== "string") {
                    for (const file of previousTask.previousProductionFileExternal) {
                        previousProductionFileExternal = [...previousProductionFileExternal];
                        // we remove the doublon here
                        if (previousProductionFileExternal.indexOf(file) === -1) {
                            previousProductionFileExternal.push(file);
                        }
                    }
                    dataToUpdate.previousProductionFileExternal = `{${previousProductionFileExternal}}`;
                } else {
                    const withoutDuplicate = [];
                    for (const file of previousTask.previousProductionFileExternal) {
                        if (previousProductionFileExternal.indexOf(file) === -1) {
                            withoutDuplicate.push(file);
                        }
                    }
                    if (withoutDuplicate.length > 0) {
                        dataToUpdate.previousProductionFileExternal = `{${withoutDuplicate.join(",")},${previousProductionFileExternal.substring(1)}`;
                    } else {
                        dataToUpdate.previousProductionFileExternal = previousProductionFileExternal;
                    }
                }
            }
        }
        if (previousTask.commentTask) {
            let comment = task.comment || "";
            if (comment !== "") {
                comment += "\n\n";
            }
            comment += await getCommentTask(dataProvider, previousTask);
            dataToUpdate.comment = comment;
        }
    }
    if (!await checkNeedStayWaiting(dataProvider, stage, productionUnitId, previousTaskId)) {
        dataToUpdate["availableDate"] = new Date();
        dataToUpdate["taskStatus"] = "available";
        // send mail
        sendMailTaskAvailable(task);
    }
    if (Object.keys(dataToUpdate).length > 0) {
        // We should update
        await update(dataProvider, "task", task.id, dataToUpdate, task);
    }
};

const createSimpleTask = async (dataProvider: DataProvider, stageId: Identifier, productionUnitId: Identifier, currentTask: Task, where: string) => {
    // On regarde si une tâche n'est pas déjà en attente
    const task = await findOne(dataProvider, "task", { stageId, productionUnitId, taskStatus: "waiting" });
    if (task) {
        const stage = await findOne(dataProvider, "stage", { id: stageId });
        await updateTask(dataProvider, task, stage, productionUnitId, currentTask);
    } else {
        const stage = await findOne(dataProvider, "stage", { id: stageId });
        const productionUnit = await findOne(dataProvider, "production_unit", { id: productionUnitId });
        const lot = await findOne(dataProvider, "lot", { id: productionUnit.lotId });
        const projectId = lot.projectId;
        const project = await findOne(dataProvider, "project", { id: projectId });
        const stageInfos = await findOne(dataProvider, "stage_infos", { projectId, stageId });
        const ratioInMinutes = (stageInfos.duration * coefficientInMinutes[stageInfos.unitTime]) / stageInfos.quantity;
        await createTask(dataProvider, stage, project, lot, productionUnit, ratioInMinutes, stageInfos, currentTask, where);
    }
};

export const createTasks = async (dataProvider: DataProvider, lot: Lot, project: Project) => {
    const projectId = project.id;
    const listStageStart = [];
    await findAndAction(dataProvider, "stage", { workflowId: project.workflowId, isStarting: true }, (data) => {
        listStageStart.push(data);
    });
    const lotId = lot.id;
    // For each start stage
    for (const stage of listStageStart) {
        const stageId = stage.id;
        const stageInfos = await findOne(dataProvider, "stage_infos", { projectId, stageId });
        const ratioInMinutes = (stageInfos.duration * coefficientInMinutes[stageInfos.unitTime]) / stageInfos.quantity;
        await findAndAction(dataProvider, "production_unit", { lotId }, async (productionUnit: ProductionUnit) => {
            await createTask(dataProvider, stage, project, lot, productionUnit, ratioInMinutes, stageInfos);
        });
    }
};

export const createNextTasks = async (dataProvider: DataProvider, previousTask: Task, where: string) => {
    const id: Identifier = previousTask.stageId;
    const productionUnitId: Identifier = previousTask.productionUnitId;
    const stage = await findOne(dataProvider, "stage", { id });
    const whereObject = stage[where];
    if (!whereObject || whereObject.length === 0) {
        await productionUnitIsMaybeDone(dataProvider, productionUnitId, previousTask.quantityTask || previousTask.quantity);
    } else {
        for (const stageId of whereObject) {
            await createSimpleTask(dataProvider, stageId, productionUnitId, previousTask, where);
        }
    };
}

const taskSubmit = async (form, status: string, dateName = "endDate") => {
    form.change("taskStatus", status);
    form.change(dateName, new Date());
    await form.submit();
};

export const taskAssign = (form, notify, dataProvider, translate) => formAction(form, notify, async () => {
    await taskSubmit(form, "assigned", "assignedDate");
    const values = form.getState().values;
    const userAssigned = await getOne(dataProvider, "user", values.assignTo);
    sendMail("task/assigned", {
        to: userAssigned.email,
        fullname: userAssigned.fullname,
        taskId: values.id,
        stageName: values.stage.name,
        productionName: values.production_unit.name,
        lotName: values.production_unit.lot.name,
        projectName: values.production_unit.lot.project.name,
        unitProd: values.production_unit.lot.project.unitProd,
        unitMeasure: values.production_unit.lot.project.unitMeasure,
        quantity: values.quantity,
        estimateTime: translateTime(translate, values.estimateTime)
    });
});

export const taskComplete = (form, notify, dataProvider) => formAction(form, notify, async () => {
    const values = form.getState().values;
    const needNextTeam = values.team_next.length > 0 && !values.nextTeamAssignTo;
    if (needNextTeam) {
        notify("resources.task.validate.needNextTeam", {
            type: "error",
            autoHideDuration: 20000
        });
    } else {
        await taskSubmit(form, "completed");
        await createNextTasks(dataProvider, values, "outgoing");
    }
});

export const taskReject = (form, notify, dataProvider: DataProvider) => formAction(form, notify, async () => {
    const values = form.getState().values;
    const needRejectTeam = values.team_reject.length > 0 && !values.rejectTeamAssignTo;
    const comment = values.commentTask;
    if (needRejectTeam) {
        notify("resources.task.validate.needRejectTeam", {
            type: "error",
            autoHideDuration: 20000
        });
    } else if (!comment) {
        notify("resources.task.validate.needComment", {
            type: "error",
            autoHideDuration: 20000
        });
    } else {
        await taskSubmit(form, "rejected");
        await createNextTasks(dataProvider, values, "outgoingError");
        const parentsTaskId = values.parentTaskId;
        if (parentsTaskId && parentsTaskId.length > 0) {
            for (const id of parentsTaskId) {
                const task = await findOne(dataProvider, "task", { id });
                const userRejected = await getOne(dataProvider, "user", task.assignTo);
                sendMail("task/rejected", {
                    to: userRejected.email,
                    fullname: userRejected.fullname,
                    taskId: id,
                    stageName: task.stage.name,
                    productionName: task.production_unit.name,
                    lotName: task.production_unit.lot.name,
                    projectName: task.production_unit.lot.project.name,
                    unitProd: task.production_unit.lot.project.unitProd,
                    unitMeasure: task.production_unit.lot.project.unitMeasure,
                    comment
                });
            }
        }
    }
});

export const taskCancel = async (form, dataProvider: DataProvider) => {
    let data = form.getState().initialValues;
    await update(dataProvider, "task", data.id, {
        taskStatus: "cancelled",
        endDate: new Date()
    }, data);
    return await createNextTasks(dataProvider, form.getState().values, "incoming");
};

const DATA_UNASSIGN = {
    taskStatus: "available",
    assignedDate: null,
    assignTo: null
};

export const taskUnassign = (form, notify, dataProvider: DataProvider) => async () => {
    let data = form.getState().initialValues;
    await update(dataProvider, "task", data.id, DATA_UNASSIGN, data);
    notify("resources.task.success.unassign", { smart_count: 1 });
};

export const taskBulkUnassign = async (dataProvider: DataProvider, ids: Identifier[]) => {
    return dataProvider.updateMany("task", {
        ids,
        data: DATA_UNASSIGN
    });
};

export const taskBulkAssignMyself = async (dataProvider: DataProvider, ids: Identifier[], assignTo: Identifier) => {
    const assignedDate = new Date();
    let hasSubproject = false;
    await dataProvider.getMany("task", { ids }).then((r) => {
        for (const t of r.data) {
            if (t.subprojectLotId) {
                hasSubproject = true;
                break;
            }
        }
    });
    if (hasSubproject) {
        throw Error("resources.task.error.bulkAssignMyselfSubproject");
    } else {
        return dataProvider.updateMany("task", {
            ids,
            data: {
                taskStatus: "assigned",
                assignedDate,
                assignTo
            }
        });
    }
};

export const taskBulkForceGo = async (dataProvider: DataProvider, ids: Identifier[]) => {
    await dataProvider.updateMany("task", {
        ids,
        data: {
            taskStatus: "completed",
            endDate: new Date()
        }
    });
    // Création des tachesO
    for (const id of ids) {
        const task = await getOne(dataProvider, "task", id);
        await createNextTasks(dataProvider, task, "outgoing");
    }
};

export const taskBulkForceBack = async (dataProvider: DataProvider, ids: Identifier[]) => {
    await dataProvider.updateMany("task", {
        ids,
        data: DATA_UNASSIGN
    });
    // Réouverture des production_unit si fermées
    const productionUnitIds = [];
    await findAndAction(dataProvider, "production_unit", {
        tasks: filter("id", ids, "_in"),
        tracking: "completed"
    }, ({ id }) => {
        productionUnitIds.push(id);
    });
    if (productionUnitIds.length > 0) {
        await dataProvider.updateMany("production_unit", {
            ids: productionUnitIds,
            data: {
                tracking: "inProgress"
            }
        });
        const lotIds = [];
        await findAndAction(dataProvider, "lot", {
            production_unit: filter("id", productionUnitIds, "_in"),
            tracking: "completed"
        }, ({ id }) => {
            lotIds.push(id);
        });
        if (lotIds.length > 0) {
            await dataProvider.updateMany("lot", {
                ids: lotIds,
                data: {
                    tracking: "inProgress"
                }
            });
        }
    }
};

export const useHandleChange = (displayedFilters, filterValues, setFilters) => {
    const unselectAll = useUnselectAll();
    return useCallback((event: React.ChangeEvent<{}>, value: any) => {
        unselectAll("task")
        setFilters && setFilters(
            { ...filterValues, taskStatus: value === "ended" ? ["completed", "rejected", "cancelled"] : value },
            displayedFilters
        );
    }, [displayedFilters, filterValues, setFilters, unselectAll]);
};